import {BasicDataResourceToQueriesMap} from '@/modules/basic-data/constants'
import {ListBasicDataInput} from '@cypro/engine/queries'
import {BasicDataItem, BasicDataResource} from '@cypro/engine/schema'
import {type GetPageQuery, type GetSingleQuery} from '@cypro/ui/utils/zero-virtualizer'
import {useCallback, useMemo, useState} from 'react'

type BasicDataStartRow = Pick<BasicDataItem, 'id' | 'createdAt' | 'name'>

interface Config<R extends BasicDataResource> extends Omit<Partial<ListBasicDataInput>, 'cursor'> {
  resource: R
  hasProtocols?: boolean
}

export function useGetInfiniteBasicDataItems<R extends BasicDataResource>({
  sort = {dir: 'asc', field: 'name'},
  filters,
  limit = 25,
  resource,
  hasProtocols,
}: Config<R>) {
  const queries = BasicDataResourceToQueriesMap[resource]

  const [search, setSearch] = useState('')

  const listContextParams = useMemo(
    () => ({sort, filters: {status: ['active'] as const, ...filters}, limit, search, resource, hasProtocols}),
    // oxlint-disable-next-line react-hooks/exhaustive-deps
    [sort?.dir, sort?.field, filters?.search, filters?.status, resource, hasProtocols, limit, search]
  )

  const getPageQuery: GetPageQuery<BasicDataItem, BasicDataStartRow> = useCallback(
    ({limit: pageLimit, start, dir}) => {
      const cursor =
        start?.id && start?.createdAt
          ? {
              id: start.id,
              direction: dir,
              createdAt: start.createdAt,
              name: start.name,
            }
          : undefined

      return {
        query: queries.listItems({
          cursor,
          limit: pageLimit,
          sort,
          filters: {status: ['active'] as const, ...filters},
          hasProtocols,
        }),
      }
    },
    // oxlint-disable-next-line react-hooks/exhaustive-deps
    [listContextParams]
  )

  // @ts-expect-error schema type mismatch (works at runtime)
  const getSingleQuery: GetSingleQuery<BasicDataItem> = useCallback(
    ({id}) => ({
      query: queries.getItem(id),
    }),
    // oxlint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  const toStartRow = useCallback(
    (row: BasicDataItem): BasicDataStartRow => ({
      id: row.id,
      createdAt: row.createdAt,
      name: row.name,
    }),
    []
  )

  const transformToSelectItem = useCallback(
    (row: BasicDataItem) => ({
      label: row.name,
      value: row.id,
    }),
    []
  )

  return {
    search,
    onSearchChange: setSearch,
    getPageQuery,
    getSingleQuery,
    toStartRow,
    listContextParams,
    transformToSelectItem,
  }
}
